import {Expressions, Plugins} from '@packages/systems/wfdl';
import {ELiteral, type Expression} from '@packages/systems/wfdl-ast';
import {namesEqual, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {getObject, getString} from '@packages/utilities/json';
import {isModalAction} from './modal/constants';
import {getModalTargetReferenceForRead} from './modal/expression';
import {isPopoverAction} from './popover/actions';
import {getPopoverTargetReference} from './popover/expression';
import type {OverlayTargetReference} from './references';

export const LINK_TYPE = qualify(Plugins.name('Basic'), typeName('Link'));

/** Reads the overlay target a Link literal holds, or null if it holds none. */
export const getOverlayTargetReferenceFromLinkLiteral = (
  expression: Expression
): OverlayTargetReference | null => {
  const literal = Expressions.getLiteral(expression);
  if (!literal || !namesEqual(literal.name, LINK_TYPE)) return null;

  const linkValue = literal.value;
  const linkData = linkValue ? getObject(linkValue) : undefined;
  const mode = linkData?.mode ? getString(linkData.mode) : undefined;
  if (!linkData || (mode !== 'popover' && mode !== 'modal')) {
    return null;
  }

  if (mode === 'modal') {
    const componentKey = linkData.modalTargetComponent
      ? getString(linkData.modalTargetComponent)
      : undefined;
    const targetId = linkData.modalTarget
      ? getString(linkData.modalTarget)
      : undefined;
    if (!componentKey || !targetId) return null;

    const action = linkData.modalAction
      ? getString(linkData.modalAction)
      : undefined;
    return {
      kind: 'modal',
      componentKey,
      targetId,
      action: isModalAction(action) ? action : 'show',
    };
  }

  const componentKey = linkData.popoverTargetComponent
    ? getString(linkData.popoverTargetComponent)
    : undefined;
  const targetId = linkData.popoverTarget
    ? getString(linkData.popoverTarget)
    : undefined;
  if (!componentKey || !targetId) return null;

  const action = linkData.popoverAction
    ? getString(linkData.popoverAction)
    : undefined;
  return {
    kind: 'popover',
    componentKey,
    targetId,
    action: isPopoverAction(action) ? action : 'toggle',
  };
};

/** Projects a Popover or Modal target Call into the Link literal the Link editor reads. */
export const getOverlayLinkLiteral = (
  expression: Expression
): Expression | null => {
  const reference = getPopoverTargetReference(expression);
  if (reference) {
    return ELiteral({
      name: LINK_TYPE,
      value: {
        mode: 'popover',
        popoverTarget: reference.targetId,
        popoverTargetComponent: reference.componentKey,
        popoverAction: reference.action,
      },
    });
  }

  const modalReference = getModalTargetReferenceForRead(expression);
  if (!modalReference) return null;

  return ELiteral({
    name: LINK_TYPE,
    value: {
      mode: 'modal',
      modalTarget: modalReference.targetId,
      modalTargetComponent: modalReference.componentKey,
      modalAction: modalReference.action,
    },
  });
};
